import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { getApi, postApi } from '../../services/apiServices';

const templates = [
    {
        id: 1,
        name: 'Segment A — Installed, never started resume',
        subject: 'the right person can\'t find you yet',
        preheader: 'Your resume is what makes a real match possible — and it\'s quick to set up.',
        body: `Hi {{first_name}},

You downloaded Resumate for a reason. Maybe you're done with surface-level apps and actually want to meet someone who gets you.

Here's the thing — that person could be on Resumate right now. But they can't find you until your resume is there. And it's not a chore: just a few honest questions about your values, your interests, and what you're really looking for. No pressure to sound perfect. Most people finish in about 3 minutes.

That's all it takes to start matching with people who want the same things you do.`,
        ps: 'P.S. The more real you are, the better your matches — so just answer like yourself.'
    },
    {
        id: 2,
        name: 'Segment B — Had a resume, then deleted it',
        subject: 'your resume\'s empty — want to rebuild it?',
        preheader: 'You did this once already — a fresh one only takes a few minutes.',
        body: `Hi {{first_name}},

You had a resume on Resumate, and at some point you cleared it out. Maybe it didn't feel quite right, or you wanted a fresh start — either way, that's completely fine.

But right now your profile is empty, which means the people who'd actually be a good fit for you can't find you. The good news: you've done this before, so rebuilding is even faster the second time. A few honest questions about your values, interests, and what you're looking for — around 3 minutes — and you're back in.

A fresh start might be exactly what gets you the right match.`,
        ps: 'P.S. Want it to feel different this time? Just answer as honestly as you can — that\'s what drives the best matches.'
    },
    {
        id: 3,
        name: 'Segment C — Added profile picture only',
        subject: 'a photo isn\'t enough to match',
        preheader: 'You\'ve started — finishing your resume is what makes real matches happen.',
        body: `Hi {{first_name}},

You've added your photo — nice, that's the first step done. But honestly? A picture alone won't help you meet the right person.

What actually creates a real connection is the rest of your resume — your values, your interests, what you're genuinely looking for. That's what Resumate uses to match you with people who fit, not just people nearby. And it's quick: a few easy questions, around 3 minutes, and you're in.

You've come this far. The person worth meeting could be on the other side of those few minutes.`,
        ps: 'P.S. Everything you\'ve added so far is saved — just open the app and pick up where you left off.'
    }
];

function CampaignEmail() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const limit = 10;
    const [filters, setFilters] = useState({
        search: ''
    });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [templateDropdown, setTemplateDropdown] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const templateRef = useRef(null);

    const previewModalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '620px',
            maxHeight: '80vh',
            borderRadius: '8px',
            padding: '0',
            overflow: 'hidden',
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
        },
    };

    const getProfileImageUrl = (user) => {
        const hasUserImage = user?.user_image;
        const hasUserImages = Array.isArray(user?.User_Images) && user.User_Images.length > 0 && user.User_Images[0]?.image;
        if (hasUserImage) return user.user_image;
        if (hasUserImages) return user.User_Images[0].image;
        return window.getPublicUrl ? window.getPublicUrl('/images/john.svg') : '/images/john.svg';
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setCurrentPage(1);
        setFilters(prev => ({
            ...prev,
            search: e.target.value
        }));
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (filters.search) params.append('search', filters.search);
            if (activeTab === 'active') {
                params.append('status', '1');
            } else if (activeTab === 'blocked') {
                params.append('status', '2');
            } else if (activeTab === 'deleted') {
                params.append('status', '3');
            } else if (activeTab === 'pending') {
                params.append('status', '0');
            }

            params.append('page', currentPage);
            params.append('limit', limit);

            const url = `users?${params.toString()}`;
            const response = await getApi(url);

            if (response?.statusCode === 200) {
                setUsers(response.data || []);
                const total = response.totalCount || 0;
                setTotalUsers(total);
                setTotalPages(Math.ceil(total / limit) || 1);
            } else {
                setError(response?.message || 'Failed to fetch users');
            }
        } catch (error) {
            setError('Error fetching users. Please try again.');
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }, [filters.search, activeTab, currentPage]);

    useEffect(() => {
        if (filters.search !== '') {
            const timeoutId = setTimeout(() => {
                fetchUsers();
            }, 500);
            return () => clearTimeout(timeoutId);
        }

        fetchUsers();
    }, [filters.search, activeTab, currentPage, fetchUsers]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (templateRef.current && !templateRef.current.contains(event.target)) {
                setTemplateDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(users.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleTemplateSelection = (templateId) => {
        setSelectedTemplate(templateId);
        setTemplateDropdown(false);
    };

    const getTemplateDisplayText = () => {
        if (selectedTemplate) {
            const selected = templates.find(t => t.id === selectedTemplate);
            return selected ? selected.name : 'Select Template';
        }
        return 'Select Template';
    };

    const getSelectedTemplateData = () => {
        return templates.find(t => t.id === selectedTemplate) || null;
    };

    const getPreviewName = () => {
        if (selectedUsers.length === 0) return 'there';
        const firstUser = users.find(u => u.id === selectedUsers[0]);
        if (!firstUser?.full_name) return 'there';
        return firstUser.full_name.split(' ')[0];
    };

    const replaceVars = (text) => {
        return text.replace(/Hi \{\{first_name\}\}/g, 'Hi there');
    };

    const handleSendMail = async () => {
        if (selectedUsers.length === 0) {
            toast.error('Please select at least one user');
            return;
        }
        if (!selectedTemplate) {
            toast.error('Please select a template');
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await postApi('/profile-reminder-emails', {
                user_ids: selectedUsers,
                template_key: selectedTemplate
            }, false);
            if (response?.statusCode === 200) {
                const missingIds = response?.data?.missing_user_ids || response?.missing_user_ids;
                if (missingIds && missingIds.length > 0) {
                    toast.warning(`Email queued. ${missingIds.length} user(s) skipped (no email on file).`);
                } else {
                    toast.success(response?.message || 'Campaign email queued successfully!');
                }
                setSelectedUsers([]);
                setSelectedTemplate('');
                setIsPreviewOpen(false);
            } else {
                toast.error(response?.message || 'Failed to send campaign email');
            }
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Failed to send campaign email. Please try again.';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const templateData = getSelectedTemplateData();

    return (
        <div className="right-section campaign-email-page">
            <div className="customer-top">
                <div className="customer-top-left">
                    <ul>
                        <li><a href="#" className={activeTab === 'all' ? 'active' : ''} onClick={(e) => { e.preventDefault(); handleTabChange('all'); }}>All</a></li>
                        <li><a href="#" className={activeTab === 'active' ? 'active' : ''} onClick={(e) => { e.preventDefault(); handleTabChange('active'); }}>Active</a></li>
                        <li><a href="#" className={activeTab === 'blocked' ? 'active' : ''} onClick={(e) => { e.preventDefault(); handleTabChange('blocked'); }}>Blocked</a></li>
                        <li><a href="#" className={activeTab === 'deleted' ? 'active' : ''} onClick={(e) => { e.preventDefault(); handleTabChange('deleted'); }}>Deleted</a></li>
                        <li><a href="#" className={activeTab === 'pending' ? 'active' : ''} onClick={(e) => { e.preventDefault(); handleTabChange('pending'); }}>Pending</a></li>
                    </ul>
                </div>

                <div className="customer-top-right">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by name / email / phone"
                        value={filters.search}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            {loading && (
                <div className="loader-overlay">
                    <span className="loader"></span>
                </div>
            )}

            <div className="customer-tablle-main">
                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '5%' }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={users.length > 0 && selectedUsers.length === users.length}
                                />
                            </th>
                            <th className="uid">Sr No</th>
                            <th className="name" scope="col">Name</th>
                            <th className="email" scope="col">Email</th>
                            <th className="status" scope="col">Status</th>
                            <th className="registered-on" scope="col">Last Active</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4">
                                    <div className="text-muted">Loading users...</div>
                                </td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4 text-danger">{error}</td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4">No users found.</td>
                            </tr>
                        ) : (
                            users.map((user, index) => (
                                <tr key={user.id || index} className={selectedUsers.includes(user.id) ? 'selected-row' : ''}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={() => handleSelectUser(user.id)}
                                        />
                                    </td>
                                    <td className="uid">{(currentPage - 1) * limit + index + 1}</td>
                                    <td className="name">
                                        <div className="table-mails">
                                            <span className="profile-img">
                                                <img
                                                    src={getProfileImageUrl(user)}
                                                    alt={user.full_name || "User"}
                                                />
                                            </span>
                                            <div>
                                                <h3>{user.full_name || "N/A"}</h3>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="email">
                                        <div>
                                            <div>{user.email || ""}</div>
                                            <div>{user.phone_number || ""}</div>
                                        </div>
                                    </td>
                                    <td className="status">
                                        {user.status === "2" ? (
                                            <button className="btn btn-status btn-status-block">
                                                <img src={window.getPublicUrl('/images/block-dots.svg')} alt="Blocked" />
                                                Blocked
                                            </button>
                                        ) : user.status === "3" ? (
                                            <button className="btn btn-status btn-status-block">
                                                <img src={window.getPublicUrl('/images/block-dots.svg')} alt="Deleted" />
                                                Deleted
                                            </button>
                                        ) : user.status === "1" ? (
                                            <button className="btn btn-status btn-status-primary">
                                                <img src={window.getPublicUrl('/images/dot.svg')} alt="Active" />
                                                Active
                                            </button>
                                        ) : (
                                            <button className="btn btn-status btn-status-incomplete">
                                                <img src={window.getPublicUrl('/images/inactive-dots.svg')} alt="Pending" />
                                                Pending
                                            </button>
                                        )}
                                    </td>
                                    <td className="registered-on">
                                        {(user.last_active || user.updatedAt) ? new Date(user.last_active || user.updatedAt).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        }) : "N/A"}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination-wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                    <div className="pagination-info" style={{ fontSize: '14px', color: '#888', fontWeight: '400' }}>
                        Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, totalUsers)} of {totalUsers} users
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                            className="btn"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', background: currentPage === 1 ? '#f5f5f5' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: currentPage === 1 ? '#ccc' : '#E94057', padding: 0 }}
                        >
                            &lsaquo;
                        </button>
                        {(() => {
                            const pages = [];
                            let startPage = Math.max(1, currentPage - 2);
                            let endPage = Math.min(totalPages, startPage + 4);
                            if (endPage - startPage < 4) {
                                startPage = Math.max(1, endPage - 4);
                            }
                            if (startPage > 1) {
                                pages.push(
                                    <button key={1} className="btn" onClick={() => setCurrentPage(1)}
                                        style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', color: '#333', cursor: 'pointer', padding: 0, fontWeight: '500' }}>1</button>
                                );
                                if (startPage > 2) {
                                    pages.push(<span key="start-dots" style={{ padding: '0 4px', color: '#999', fontSize: '14px' }}>...</span>);
                                }
                            }
                            for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                    <button
                                        key={i}
                                        className="btn"
                                        onClick={() => setCurrentPage(i)}
                                        style={{
                                            width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '14px',
                                            border: i === currentPage ? '1px solid #E94057' : '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            background: i === currentPage ? '#E94057' : '#fff',
                                            color: i === currentPage ? '#fff' : '#333',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            padding: 0
                                        }}
                                    >
                                        {i}
                                    </button>
                                );
                            }
                            if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                    pages.push(<span key="end-dots" style={{ padding: '0 4px', color: '#999', fontSize: '14px' }}>...</span>);
                                }
                                pages.push(
                                    <button key={totalPages} className="btn" onClick={() => setCurrentPage(totalPages)}
                                        style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', color: '#333', cursor: 'pointer', padding: 0, fontWeight: '500' }}>{totalPages}</button>
                                );
                            }
                            return pages;
                        })()}
                        <button
                            className="btn"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', background: currentPage === totalPages ? '#f5f5f5' : '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: currentPage === totalPages ? '#ccc' : '#E94057', padding: 0 }}
                        >
                            &rsaquo;
                        </button>
                    </div>
                </div>
            )}

            <div className="campaign-email-footer">
                <div className="campaign-email-footer-left">
                    {selectedUsers.length > 0 && (
                        <span className="selected-count">{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected</span>
                    )}
                    <div className="select-option" ref={templateRef}>
                        <div className="selctions-option position-relative">
                            <div
                                className={`dropdown-cs ${templateDropdown ? 'before-change-cs' : ''}`}
                                onClick={() => setTemplateDropdown(!templateDropdown)}
                            >
                                {getTemplateDisplayText()}
                            </div>

                            {templateDropdown && (
                                <div className="select-dropdown w-100">
                                    <ul>
                                        {templates.map(template => (
                                            <li
                                                key={template.id}
                                                className={selectedTemplate === template.id ? 'active-template' : ''}
                                                onClick={() => handleTemplateSelection(template.id)}
                                            >
                                                <span>{template.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                    {selectedTemplate && (
                        <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setIsPreviewOpen(true); }}>
                            Preview
                        </a>
                    )}
                </div>
                <button
                    type="button"
                    className="btn btn-save"
                    onClick={handleSendMail}
                    disabled={isSubmitting || selectedUsers.length === 0 || !selectedTemplate}
                >
                    {isSubmitting ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            Sending...
                        </>
                    ) : (
                        "Send Mail"
                    )}
                </button>
            </div>

            <Modal
                isOpen={isPreviewOpen}
                onRequestClose={() => setIsPreviewOpen(false)}
                style={previewModalStyles}
                contentLabel="Template Preview"
            >
                {templateData && (
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{templateData.name}</h3>
                            <button type="button" onClick={() => setIsPreviewOpen(false)} className="close" data-dismiss="modal" aria-label="Close">
                                <img src={window.getPublicUrl('/images/close-icon.svg')} alt="Close" />
                            </button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div className="template-preview-row">
                                <label>Subject</label>
                                <p style={{ textTransform: 'capitalize' }}>{templateData.subject}</p>
                            </div>
                            <div className="template-preview-row">
                                <label>Preheader</label>
                                <p>{templateData.preheader}</p>
                            </div>
                            <div className="template-preview-row">
                                <label>Body</label>
                                <p style={{ whiteSpace: 'pre-line' }}>{replaceVars(templateData.body)}</p>
                            </div>
                            <div className="template-preview-row">
                                <p style={{ fontStyle: 'italic' }}>{templateData.ps}</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={() => setIsPreviewOpen(false)}>Close</button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default CampaignEmail;
